/**
 * Locale-switch offer helpers for the chat widget.
 *
 * The chatbot always answers in the page locale — its knowledge base is
 * retrieved in that language, so answering in another would mean translating
 * medical terms and prices on the fly. When a customer writes in a different
 * *supported* language it instead emits `suggest_locale_switch`, which glue
 * forwards to the widget as an SSE event and we turn into a one-click offer.
 * The customer chooses; the bot never switches on its own.
 *
 * Pure on purpose — the routing and validation rules are the part worth
 * testing, and they must not depend on React or Next's router.
 */

/**
 * Decide whether an offer is worth showing, and for which locale.
 *
 * Returns null when the offer should be dropped. Three reasons:
 *  - the suggestion is empty or malformed;
 *  - it names the locale the customer is already reading (pure noise);
 *  - the current country does not serve that language, in which case a link
 *    would 404. Country/language pairs are not uniform: CH serves de/fr/en,
 *    NL serves nl/en, so "fr" is a real offer on a Swiss page and a dead link
 *    on a Dutch one.
 *
 * glue already validates the code against the supported set, but this runs on
 * untrusted model output that reaches a URL — validate on both sides.
 */
export function resolveSwitchLocale(
  suggested: string | undefined | null,
  currentLocale: string,
  countryLanguages: readonly string[]
): string | null {
  const target = (suggested ?? "").trim().toLowerCase()
  if (!target) return null
  if (target === currentLocale.toLowerCase()) return null

  const match = countryLanguages.find((l) => l.toLowerCase() === target)
  return match ?? null
}

/**
 * Swap the locale segment of a `/{country}/{locale}/...` path, preserving the
 * country, the rest of the path, and any query string or hash.
 *
 * Returns null when the path does not have the expected shape, so the caller
 * can drop the offer rather than navigate somewhere wrong.
 */
export function switchLocaleInPath(
  pathname: string,
  targetLocale: string
): string | null {
  if (!pathname.startsWith("/")) return null

  // Split off query/hash first so a locale-looking string in a query param
  // cannot be rewritten.
  const queryIndex = pathname.search(/[?#]/)
  const path = queryIndex === -1 ? pathname : pathname.slice(0, queryIndex)
  const suffix = queryIndex === -1 ? "" : pathname.slice(queryIndex)

  const segments = path.split("/")
  // ["", country, locale, ...rest] — a bare "/" or "/ch" has no locale segment.
  if (segments.length < 3 || !segments[1] || !segments[2]) return null

  segments[2] = targetLocale
  return segments.join("/") + suffix
}

/**
 * Human-readable language name, written in the language of the page the
 * customer is currently on ("Englisch" on a German page, "English" on an
 * English one). Falls back to the raw code if the runtime cannot resolve it.
 *
 * This exists because `ui.switch_locale` interpolates a `{locale}` value that
 * was previously the raw code — rendering "We have this page in en — want to
 * switch?".
 */
export function languageDisplayName(
  targetLocale: string,
  displayIn: string
): string {
  try {
    const name = new Intl.DisplayNames([displayIn], { type: "language" }).of(
      targetLocale
    )
    if (!name || name.toLowerCase() === targetLocale.toLowerCase()) {
      return targetLocale
    }
    // Locale-aware capitalisation: German capitalises language names, English
    // does too, French does not — Intl already returns the conventional form,
    // so only the first character needs normalising for sentence starts.
    return name
  } catch {
    return targetLocale
  }
}
